import Task from './Task';
import { createError, getApiError } from '../error';
import { normalizeIdentifier} from "../util"

const getRecapResponseSetUrl = '/recap_response_set';
const uploadRecapResponseFile = '/recap_response_set/upload';

class RecapResponseSaveTask extends Task {
  constructor(
    $q,
    APIService,
    DataSyncService,
    LogService,
    AnalyticsService,
    RecapResponseService,
    DocumentService
  ) {
    super();
    this.$q = $q;
    this.APIService = APIService;
    this.DataSyncService = DataSyncService;
    this.RecapResponseService = RecapResponseService;
    this.LogService = LogService;
    this.AnalyticsService = AnalyticsService;
    this.DocumentService = DocumentService;
    this._recapResponseData = null;
    this._allDocumentDataToUpload = [];
    this._trackDocumentIdsForFieldResponses = {};
  }

  /**
   * Public properties
   */

  set serializedDocumentsToUpload(docs) {
    this._serializedDocumentsToUpload = docs;
  }

  get serializedDocumentsToUpload() {
    return this._serializedDocumentsToUpload;
  }

  set recapResponseData(data) {
    this._recapResponseData = data;
  }

  get recapResponseData() {
    return this._recapResponseData;
  }

  get canExecute() {
    return this.APIService.isOnline;
  }

  get updatingObjectTypesAndIds() {
    const updating = [];
    if (this.recapResponseData) {
      updating.push(['RecapResponse', this.recapResponseData.id]);
    }
    return updating;
  }

  set recapResponseDiagnosticsData(data) {
    this._recapResponseDiagnosticsData = data;
  }

  get recapResponseDiagnosticsData() {
    return this._recapResponseDiagnosticsData;
  }

  /**
   * Public methods
   */

  populate(data) {
    this.staffUserId = data.staffUserId;
    this.serializedDocumentsToUpload = data.serializedDocumentsToUpload || [];
    this.recapResponseData = data.recapResponseData;
    this.recapResponseDiagnosticsData = data.recapResponseDiagnosticsData;
  }

  serialize() {
    return {
      staffUserId: this.staffUserId,
      serializedDocumentsToUpload: this.serializedDocumentsToUpload,
      recapResponseData: this.recapResponseData,
      recapResponseDiagnosticsData: this.recapResponseDiagnosticsData,
      ...super.serialize(),
    };
  }

  setRecapResponse(recapResponse) {
    this.staffUserId = recapResponse.staffing_users_id;

    // Serialize documents for upload
    this.serializedDocumentsToUpload = this._serializeDocumentsForUpload(recapResponse);

    // Serialize recap response
    this.recapResponseData = this.RecapResponseService.serialize(recapResponse);

    // Serialize diagnostic data
    this.recapResponseDiagnosticsData = this.RecapResponseService.getFullDiagnosticInfoForRecapResponse(recapResponse);
  }

  execute() {
    const currentRecapResponseSetId = normalizeIdentifier(this.recapResponseData.id);
    const staffingUser = this.DataSyncService.staffUser;
    const uploadedDocumentIdsByFieldResponseId = {};
    const documentUploadPromises = this.serializedDocumentsToUpload
      .map(
        docInfo => this._uploadSerializedDocument(docInfo)
          .catch(err => {
            this.LogService.error("Error uploading document; swallowing. Document:", docInfo, "error:", err);
            return this.$q.reject(err);
          })
          .then(updatedDocInfo => {
            let {fieldResponseId, newDocumentId} = updatedDocInfo;
            if (!(fieldResponseId in uploadedDocumentIdsByFieldResponseId)) {
              uploadedDocumentIdsByFieldResponseId[fieldResponseId] = [];
            }
            uploadedDocumentIdsByFieldResponseId[fieldResponseId].push(newDocumentId);
          })
      );

    return this.$q.all([
      this.$q.all(documentUploadPromises),
      this.$q.when(this.gpsPromise),
    ])
      .then(([_, gpsCoords]) => {
        this._updateRecapResponseWithUploadedDocuments(uploadedDocumentIdsByFieldResponseId);

        if (gpsCoords) {
          this.recapResponseData.end_lat = gpsCoords.end_lat;
          this.recapResponseData.end_lon = gpsCoords.end_lon;
        }

        return this.APIService.post(getRecapResponseSetUrl, this.recapResponseData);
      })
      .catch(response => {
        this.LogService.error("Error saving recap response. Serialized response data:", this.recapResponseData, "error response:", response);
        return this.$q.reject(createError({
          error: 'ErrorSavingRecapResponse',
          message: 'Error saving recap response',
          response,
        }))
      })
      .then(response => {
        const eventName = 'RECAP_RESPONSE_SUBMITTED';
        return this.$q.when(this.recapResponseDiagnosticsData)
          .then(data => this.$q.when(this.AnalyticsService.trackEvent(
            eventName,
            data
          )))
          .catch(err => {
            this.LogService.error(`Swallowing error sending ${eventName}:`, err);
          })
          .then(() => response);
      })
      .then(response => {
        const errors = getApiError(response);
        if (errors) {
          return this.$q.reject(errors);
        }

        return this.RecapResponseService.create(response.data)
          .then(recapResponse => {
            return this.RecapResponseService.updateRecapResponseReferencesAfterSave(staffingUser, recapResponse, currentRecapResponseSetId)
              .then(() => recapResponse);
          });
      })
      .catch(err => {
        this.LogService.error("Error creating recap response after saving:", err);
        return this.$q.reject(err);
      });
  }

  /**
   * Private methods
   */

  _serializeDocumentsForUpload(recapResponse) {
    // Find all documents pending upload
    const fieldResponses = recapResponse.collectAllVisibleFieldResponses();
    const fieldResponsesWithDocuments = fieldResponses.filter(fieldResponse => fieldResponse.documentsToUpload.length > 0);
    const serializedDocumentsForUploadByField = fieldResponsesWithDocuments.map(fieldResponse => {
      const documents = fieldResponse.documentsToUpload;
      return documents.map(document => this._serializeDocumentForUpload(document, fieldResponse, recapResponse));
    });
    return serializedDocumentsForUploadByField.reduce((pre, cur) => pre.concat(cur), []);
  }

  _serializeDocumentForUpload(document, fieldResponse, recapResponse) {
    const uploadPath = uploadRecapResponseFile;
    const params = {
      events_custom_id: fieldResponse && fieldResponse.events_custom_id || null,
      events_id: recapResponse && recapResponse.events_id || null,
    };
    const uploadUrl = this.APIService.convertPathToURL(uploadPath, params);
    return {
      documentData: this.DocumentService.serialize(document),
      url: uploadUrl,
      fieldResponseId: fieldResponse.id,
    };
  }

  _uploadSerializedDocument(docInfo) {
    const { documentData, url } = docInfo;
    return this.$q.when(this.DocumentService.create(documentData))
      .then(document => {
        return this.DocumentService.upload(document, url, null, 'file')
          .then(result => {
            const data = result.data;
            const newDocumentId = data.id;
            this._documentWasUploaded(docInfo, newDocumentId);
            return {...docInfo, newDocumentId};
          })
          .catch(err => {
            this.LogService.error("Error uploading document. docInfo:", docInfo, "error:", err);
            return this.$q.reject(err);
          })
      })
  }

  _documentWasUploaded(docInfo, newDocumentId) {
    const fieldResponseId = docInfo.fieldResponseId;
    if (!(fieldResponseId in this._trackDocumentIdsForFieldResponses)) {
      this._trackDocumentIdsForFieldResponses[fieldResponseId] = [];
    }
    this._trackDocumentIdsForFieldResponses[fieldResponseId].push(newDocumentId);
  }

  /**
   * Updates recapResponseData in place to reflect new document ID's
   * @param uploadedDocumentIdsByFieldResponseId
   * @private
   */
  _updateRecapResponseWithUploadedDocuments(uploadedDocumentIdsByFieldResponseId) {
    const allFieldResponses = this.recapResponseData.form_responses
        .map(form_response => form_response.field_responses)
        .reduce((pre, cur) => pre.concat(cur), []);

    const debugData = {
      uploadedDocumentIdsByFieldResponseId,
      allFieldResponses,
      fieldResponseValueChanges: [],
    };

    allFieldResponses.forEach(fieldResponse => {
      if (fieldResponse.id in uploadedDocumentIdsByFieldResponseId) {
        // Mark document as uploaded
        let debugFieldResponseValueChangeInfo = {
          fieldResponseId: fieldResponse.id,
          fieldInstanceId: fieldResponse.fieldInstance && fieldResponse.fieldInstance.id,
          fieldId: fieldResponse.fieldInstance && fieldResponse.fieldInstance.field && fieldResponse.fieldInstance.field.id,
          currentValueJSON: JSON.stringify(fieldResponse.value),
        };
        if (fieldResponse.value && fieldResponse.value.indexOf && fieldResponse.value.indexOf('null') === -1) {
          fieldResponse.value += '|' + uploadedDocumentIdsByFieldResponseId[fieldResponse.id].join('|');
        } else {
          fieldResponse.value = uploadedDocumentIdsByFieldResponseId[fieldResponse.id].join('|');
        }
        debugFieldResponseValueChangeInfo.newValueJSON = JSON.stringify(fieldResponse.value);
        debugData.fieldResponseValueChanges.push(debugFieldResponseValueChangeInfo);
      }
    });

    this.AnalyticsService.trackEvent('UPDATE_RECAP_RESPONSE_WITH_UPLOADED_DOCUMENTS', debugData);
  }
}

export default RecapResponseSaveTask;
